/**
 * Captcha Service
 * Captcha yönetimi için merkezi service
 */

import {
  loadRecaptchaScript,
  renderRecaptchaWidget,
  resetRecaptchaWidget,
  getRecaptchaToken,
  loadRecaptchaV3Script,
  getRecaptchaV3Token,
  loadTurnstileScript,
  renderTurnstileWidget,
  resetTurnstileWidget,
  getTurnstileToken,
  removeTurnstileWidget,
} from '@/lib/captcha';

export interface CaptchaConfig {
  is_enabled: boolean;
  provider: string;
  site_key: string;
  script_url?: string;
}

export type CaptchaProvider = 'google_recaptcha_v2' | 'google_recaptcha_v3' | 'google_recaptcha_enterprise' | 'cloudflare_turnstile';

export class CaptchaService {
  private widgetId: number | string | null = null;
  private provider: CaptchaProvider | null = null;
  private containerId: string;
  private config: CaptchaConfig | null = null;

  constructor(containerId: string) {
    this.containerId = containerId;
  }

  /**
   * Captcha'yı initialize et
   */
  async initialize(config: CaptchaConfig): Promise<void> {
    if (!config.is_enabled || !config.site_key || !config.provider) {
      return;
    }

    this.config = config;
    this.provider = config.provider as CaptchaProvider;

    try {
      if (this.provider === 'google_recaptcha_v2') {
        await loadRecaptchaScript(config.script_url);
        await this.renderRecaptchaV2(config.site_key);
      } else if (this.provider === 'google_recaptcha_v3' || this.provider === 'google_recaptcha_enterprise') {
        await loadRecaptchaV3Script(config.site_key, config.script_url);
        // v3 için widget render gerekmez
      } else if (this.provider === 'cloudflare_turnstile') {
        await loadTurnstileScript(config.script_url);
        await this.renderTurnstile(config.site_key);
      }
    } catch (error) {
      console.error('[CaptchaService] Initialization error:', error);
      throw error;
    }
  }

  /**
   * reCAPTCHA v2 widget render
   */
  private async renderRecaptchaV2(siteKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Eğer zaten bir widget render edilmişse, önce onu reset et
      if (this.widgetId !== null && typeof this.widgetId === 'number') {
        try {
          resetRecaptchaWidget(this.widgetId);
          this.widgetId = null;
        } catch (error) {
          // Reset hatası - devam et
        }
      }
      
      // Container'ı önce temizle (eğer varsa)
      const existingContainer = document.getElementById(this.containerId);
      if (existingContainer) {
        existingContainer.innerHTML = '';
      }
      
      const checkContainer = setInterval(() => {
        const container = document.getElementById(this.containerId);
        if (container && siteKey) {
          clearInterval(checkContainer);
          
          // Container'ı tekrar temizle (güvenlik için)
          container.innerHTML = '';
          
          renderRecaptchaWidget(this.containerId, siteKey, () => {
            // Token callback - gerekirse kullanılabilir
          })
            .then((widgetId) => {
              this.widgetId = widgetId;
              resolve();
            })
            .catch((error) => {
              console.error('[CaptchaService] reCAPTCHA v2 render error:', error);
              reject(error);
            });
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkContainer);
        reject(new Error('Container not found'));
      }, 10000);
    });
  }

  /**
   * Turnstile widget render
   */
  private async renderTurnstile(siteKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const checkContainer = setInterval(() => {
        const container = document.getElementById(this.containerId);
        if (container && siteKey) {
          clearInterval(checkContainer);
          renderTurnstileWidget(this.containerId, siteKey, () => {
            // Token callback - gerekirse kullanılabilir
          })
            .then((widgetId) => {
              this.widgetId = widgetId;
              resolve();
            })
            .catch((error) => {
              console.error('[CaptchaService] Turnstile render error:', error);
              reject(error);
            });
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkContainer);
        reject(new Error('Container not found'));
      }, 10000);
    });
  }

  /**
   * Captcha token'ı al
   */
  async getToken(action?: string): Promise<string> {
    if (!this.config?.is_enabled || !this.provider) {
      return '';
    }

    try {
      if (this.provider === 'google_recaptcha_v2') {
        return getRecaptchaToken(this.widgetId as number | undefined) || '';
      } else if (this.provider === 'google_recaptcha_v3' || this.provider === 'google_recaptcha_enterprise') {
        if (!this.config.site_key) {
          throw new Error('reCAPTCHA v3 site key is required');
        }
        return await getRecaptchaV3Token(this.config.site_key, action || 'submit');
      } else if (this.provider === 'cloudflare_turnstile') {
        return getTurnstileToken(this.widgetId as string | undefined) || '';
      }
    } catch (error) {
      console.error('[CaptchaService] Get token error:', error);
      throw error;
    }

    return '';
  }

  /**
   * Captcha'yı reset et
   */
  reset(): void {
    if (!this.provider || !this.widgetId) {
      return;
    }

    try {
      if (this.provider === 'google_recaptcha_v2') {
        resetRecaptchaWidget(this.widgetId as number);
      } else if (this.provider === 'cloudflare_turnstile') {
        resetTurnstileWidget(this.widgetId as string);
      }
      // v3 ve Enterprise için reset gerekmez (her istekte yeni token alınır)
    } catch (error) {
      console.error('[CaptchaService] Reset error:', error);
    }
  }

  /**
   * Captcha widget'ını kaldır
   */
  remove(): void {
    if (this.provider === 'cloudflare_turnstile' && this.widgetId) {
      try {
        removeTurnstileWidget(this.widgetId as string);
      } catch (error) {
        console.error('[CaptchaService] Remove error:', error);
      }
    }
  }

  /**
   * Captcha'nın aktif olup olmadığını kontrol et
   */
  isEnabled(): boolean {
    return this.config?.is_enabled === true && !!this.config?.site_key && !!this.provider;
  }

  /**
   * Provider'ı getir
   */
  getProvider(): CaptchaProvider | null {
    return this.provider;
  }
}

